import { AnalyticsEvent, EventCategory, type PropertyValue, trackUnifiedEvent } from 'services/analytics'
import { getAnalyticsContext } from 'services/analytics/unified'

const ACTIVITY_SESSION_STORAGE_KEY = 'load_hub_analytics_activity_session_v1'
const TAB_STORAGE_KEY = 'load_hub_analytics_tab_id_v1'
const ACTIVITY_SESSION_TIMEOUT_MS = 30 * 60 * 1000
let activeActivation: { id: string; expiresAt: number } | undefined

interface StoredActivitySession {
    id: string
    lastHumanActivityAt: number
}

export type LoadHubAnalyticsProperties = Record<string, PropertyValue | null | undefined>

interface LoadHubEventOptions {
    /** Human actions create/extend the explicit 30-minute Load Hub activity session. */
    humanActivity?: boolean
    /** Operational/background events inherit a live session but never extend it. */
    actorType?: 'user' | 'system'
    initiatedFrom?: 'load_hub' | 'ai_hub' | 'settings' | 'extension' | 'background' | 'unknown'
    productArea?: 'load_hub' | 'ai_hub' | 'shared_loadboard'
    /** The surface that owns the measured operation. Web may relay extension evidence. */
    platform?: 'web' | 'extension' | 'server'
}

const createFallbackId = (): string => `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`

export const createLoadHubAnalyticsId = (prefix: string): string => {
    const value = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : createFallbackId()
    return `${prefix}_${value}`
}

const activitySessionStorageKey = (): string => {
    const context = getAnalyticsContext()
    return [ACTIVITY_SESSION_STORAGE_KEY, context.company_id ?? 'unknown_company', context.user_id ?? 'unknown_user', getLoadHubTabId()].join(':')
}

const readActivitySession = (): StoredActivitySession | null => {
    if (typeof localStorage === 'undefined') return null
    try {
        const value = localStorage.getItem(activitySessionStorageKey())
        if (!value) return null
        const parsed = JSON.parse(value) as Partial<StoredActivitySession>
        return typeof parsed.id === 'string' && typeof parsed.lastHumanActivityAt === 'number' ? (parsed as StoredActivitySession) : null
    } catch {
        return null
    }
}

const writeActivitySession = (session: StoredActivitySession): void => {
    if (typeof localStorage === 'undefined') return
    try {
        localStorage.setItem(activitySessionStorageKey(), JSON.stringify(session))
    } catch {
        // Analytics storage must never interfere with product behavior.
    }
}

const getLoadHubActivitySessionId = (humanActivity: boolean): string | undefined => {
    const now = Date.now()
    const current = readActivitySession()
    const isLive = Boolean(current && now - current.lastHumanActivityAt <= ACTIVITY_SESSION_TIMEOUT_MS)
    if (!humanActivity) return isLive ? current?.id : undefined

    const session: StoredActivitySession = {
        id: isLive && current ? current.id : createLoadHubAnalyticsId('lhs'),
        lastHumanActivityAt: now,
    }
    writeActivitySession(session)
    return session.id
}

const getLoadHubTabId = (): string => {
    if (typeof sessionStorage === 'undefined') return createLoadHubAnalyticsId('tab')
    try {
        const existing = sessionStorage.getItem(TAB_STORAGE_KEY)
        if (existing) return existing
        const created = createLoadHubAnalyticsId('tab')
        sessionStorage.setItem(TAB_STORAGE_KEY, created)
        return created
    } catch {
        return createLoadHubAnalyticsId('tab')
    }
}

export const setActiveLoadHubActivation = (id: string | undefined, firstViewAt?: number): void => {
    activeActivation = id && firstViewAt !== undefined ? { id, expiresAt: firstViewAt + 24 * 60 * 60 * 1000 } : undefined
}

const analyticsEnvironment = (): 'production' | 'staging' | 'development' => {
    const configured = process.env.REACT_APP_ENVIRONMENT?.toLowerCase()
    if (configured === 'production' || configured === 'staging' || configured === 'development') return configured
    return process.env.NODE_ENV === 'production' ? 'production' : 'development'
}

/**
 * Canonical Load Hub wrapper. It owns reserved product/platform keys so call sites
 * cannot accidentally turn a shared component into Load Hub activity or put a board
 * name in the analytics wrapper's reserved `source` property.
 */
export const trackLoadHubEvent = (action: AnalyticsEvent, properties: LoadHubAnalyticsProperties = {}, options: LoadHubEventOptions = {}): void => {
    const humanActivity = options.humanActivity ?? options.actorType !== 'system'
    const activitySessionId = getLoadHubActivitySessionId(humanActivity)
    trackUnifiedEvent({
        category: EventCategory.FEATURE_USE,
        action,
        event_id: createLoadHubAnalyticsId('evt'),
        product_area: options.productArea ?? 'load_hub',
        initiated_from: options.initiatedFrom ?? 'load_hub',
        actor_type: options.actorType ?? 'user',
        platform: options.platform ?? 'web',
        environment: analyticsEnvironment(),
        schema_version: 1,
        app_version: process.env.REACT_APP_VERSION ?? 'unknown',
        tab_id: getLoadHubTabId(),
        ...(activitySessionId ? { activity_session_id: activitySessionId } : {}),
        ...(activeActivation && activeActivation.expiresAt >= Date.now() ? { activation_id: activeActivation.id } : {}),
        ...properties,
        surface: 'load_hub',
    })
}
